import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  ArrowLeft, Clock, MapPin, Users, Calendar, CheckCircle,
  XCircle, ExternalLink, ChevronRight, Zap, Building2, AlertTriangle, BarChart2, Bell,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { VICMarginalSeatsWidget } from "../components/VICMarginalSeatsWidget";
import { VICChecklistWidget } from "../components/VICChecklistWidget";

const ELECTION_DATE = new Date("2026-11-28T08:00:00+11:00");
const ENROL_CLOSE   = new Date("2026-11-13T23:59:59+11:00");

const VIC_LEADERS = [
  {
    id: "jacinta-allan",
    name: "Jacinta Allan",
    role: "Premier of Victoria — seeking first full-term mandate",
    party: "Australian Labor Party",
    color: "#CC2529",
    slogan: "Building Victoria's Future Together",
    inParlSince: 1999,
    electorate: "Bendigo East",
    highlights: [
      "Became Premier September 2023 after Andrews resigned",
      "Suburban Rail Loop (SRL) underway — 26 new stations",
      "Free TAFE expanded — over 80,000 enrolments",
      "Big Housing Build — 12,000+ new homes committed",
      "First woman Premier of Victoria",
    ],
    criticisms: [
      "State net debt forecast to reach $187B by 2027–28",
      "Commonwealth Games cancellation cost $589M in penalties",
      "SRL costs ballooned; independent reviews questioned viability",
      "Ambulance ramping and hospital wait times persist",
    ],
  },
  {
    id: "john-pesutto",
    name: "John Pesutto",
    role: "Leader of the Opposition — challenging for Premier",
    party: "Liberal Party of Australia",
    color: "#1B4F9E",
    slogan: "A Better Deal for Victorians",
    inParlSince: 2010,
    electorate: "Hawthorn",
    highlights: [
      "Debt reduction plan — freeze new major spending",
      "Scrap the SRL's outer stages to save $50B+",
      "Stamp duty abolition for first home buyers",
      "Accountability on ambulance response times",
      "Led Liberals through internal Moira Deeming dispute",
    ],
    criticisms: [
      "Internal party divisions have distracted from policy",
      "Liberals lost 2022 by record margin — rebuilding trust",
      "Alternative infrastructure plan short on detail",
      "Some policies seen as too economically conservative",
    ],
  },
];

const VIC_ISSUES = [
  {
    id: "debt",
    title: "State Debt & Cost of Living",
    icon: "💰",
    color: "#EAB308",
    description: "Victoria carries the highest state debt in Australia — projected at $187B by 2027-28. Cost of living pressure is the defining issue for Melbourne households.",
    labor: { position: "Invest through the cycle", detail: "Allan argues the debt reflects generational investment in infrastructure and services. Pointing to Federal cost-of-living relief as complementary. Will maintain spending on hospitals and schools." },
    liberal: { position: "Debt freeze & cuts", detail: "Pesutto's central platform is halting new major borrowing, scrapping later SRL stages, and returning the budget to surplus faster. Promises to cut government waste, not services." },
    greens: { position: "Wealth tax + protect services", detail: "The Greens want to fund services through taxes on investment properties and corporations rather than borrowing. Oppose both Labor's debt accumulation and Liberal's cuts agenda." },
    verdict: "Debt will dominate the campaign — voters must decide if the spending is worthwhile or reckless.",
  },
  {
    id: "housing",
    title: "Housing Affordability",
    icon: "🏠",
    color: "#E11D48",
    description: "Melbourne's median house price has climbed above $900K. Rental vacancy rates are near record lows, with average rents up 25% since 2021.",
    labor: { position: "Supply + social housing", detail: "Big Housing Build: 12,000+ new homes across metropolitan and regional VIC. Planning reforms to allow more density near train stations. Continuing $5.3B social housing program." },
    liberal: { position: "Stamp duty reform + planning", detail: "Abolish stamp duty for first home buyers on homes up to $1M. Major planning deregulation to fast-track development. Cut the 'developer contributions' that add cost to new homes." },
    greens: { position: "Social housing + end negative gearing", detail: "The Greens push for a large state-funded social housing program and want the state to lobby for an end to negative gearing. Support stronger renters' rights and rent caps on renewals." },
    verdict: "Both major parties agree supply is the problem — they differ on whether the state should build homes or get out of the way. Greens focus on renters over ownership.",
  },
  {
    id: "srl",
    title: "Suburban Rail Loop",
    icon: "🚆",
    color: "#2563EB",
    description: "The $34–125B+ Suburban Rail Loop is the most contested infrastructure project in Victoria's history, running a 90km orbital line from Cheltenham to Melbourne Airport.",
    labor: { position: "Full steam ahead", detail: "Allan is committed to the full SRL. SRL East (Cheltenham–Box Hill) is under construction. The project is a Labor legacy investment and will unlock 70,000 new homes near stations." },
    liberal: { position: "Cancel outer stages", detail: "Pesutto would complete the under-construction SRL East to avoid penalty costs, but scrap the outer stages (North and Airport link), saving an estimated $50B+. Redirect funds to roads and hospitals." },
    greens: { position: "Support SRL + more trams & buses", detail: "Greens support the SRL in principle but want greater investment in tram extensions, bike lanes and buses alongside it. Push for free or heavily discounted public transport fares statewide." },
    verdict: "The SRL is the single biggest policy divide — VIC voters will effectively vote on whether to proceed with one of the world's most expensive rail projects.",
  },
  {
    id: "health",
    title: "Health & Ambulance Services",
    icon: "🏥",
    color: "#8B5CF6",
    description: "Victoria's mental health royal commission has driven major reform, but ambulance response times and emergency department wait times remain flashpoints.",
    labor: { position: "Record investment continuing", detail: "Committed $5.3B over 5 years to mental health reform (royal commission response). New hospitals in Footscray and Frankston. 2,000+ extra nurses and paramedics hired." },
    liberal: { position: "Ambulance targets + accountability", detail: "Legally binding ambulance response time targets with ministerial accountability. Freeze agency spending and redirect to frontline paramedics and emergency beds." },
    greens: { position: "Mental health + bulk billing focus", detail: "The Greens prioritise full implementation of the royal commission into mental health. Push for state-funded GP bulk billing clinics to ease pressure on emergency departments." },
    verdict: "Health is Allan's biggest vulnerability — Labor has invested heavily but ramping and wait times remain politically damaging.",
  },
  {
    id: "climate",
    title: "Climate & Renewable Energy",
    icon: "⚡",
    color: "#10B981",
    description: "Victoria has set a 95% renewable electricity target by 2035 and is building offshore wind zones in Bass Strait. The Hazelwood and Loy Yang A coal exits loom large.",
    labor: { position: "Accelerate transition", detail: "95% renewable target by 2035. VicGrid renewable energy zones in Gippsland, Western District and Murray. Offshore wind at Portland and Gippsland. Loy Yang A phased closure supported." },
    liberal: { position: "Reliable energy first", detail: "Supports renewables but opposes rushing coal closures before the grid is stable. Calls for a reliability standard before committing to 95% renewables. Open to gas extension as transition fuel." },
    greens: { position: "100% renewables by 2030 + no new gas", detail: "The Greens want Victoria to reach 100% renewable electricity by 2030 — 5 years ahead of Labor. Strongly oppose any gas extension or new fossil fuel projects. Push for community-owned batteries and solar." },
    verdict: "Victoria's energy transition is fast and ambitious — voters must judge whether Labor's pace is visionary or risky for power prices. Greens push for faster action.",
  },
];

const VIC_PARTIES = [
  { name: "ALP",      full: "Australian Labor Party (VIC)",     color: "#CC2529", seats2022: 56, leader: "Jacinta Allan",                      likelihood: "Favourite" },
  { name: "Liberal",  full: "Liberal Party of Australia (VIC)",  color: "#1B4F9E", seats2022: 26, leader: "John Pesutto",                        likelihood: "Challenger" },
  { name: "National", full: "The Nationals (VIC)",               color: "#006633", seats2022: 5,  leader: "Peter Walsh",                          likelihood: "Minor — regional" },
  { name: "Greens",   full: "Australian Greens (VIC)",           color: "#10B981", seats2022: 4,  leader: "Samantha Ratnam (Upper House)",         likelihood: "Balance of power" },
  { name: "Ind/Other",full: "Independents & Others",             color: "#94A3B8", seats2022: 3,  leader: "Various",                              likelihood: "Key outer seats" },
];

const VIC_POLLS = [
  { pollster: "Redbridge",   date: "Feb 2026", alp: 39, lib: 33, grn: 14, oth: 14, two_pp_alp: 56, two_pp_lib: 44 },
  { pollster: "Essential",   date: "Jan 2026", alp: 38, lib: 34, grn: 12, oth: 16, two_pp_alp: 55, two_pp_lib: 45 },
  { pollster: "Newspoll VIC",date: "Nov 2025", alp: 40, lib: 32, grn: 13, oth: 15, two_pp_alp: 57, two_pp_lib: 43 },
  { pollster: "Roy Morgan",  date: "Oct 2025", alp: 41, lib: 31, grn: 14, oth: 14, two_pp_alp: 58, two_pp_lib: 42 },
];

const VIC_TREND = [
  { month: "Oct '25", ALP: 41, Coalition: 31, Greens: 14 },
  { month: "Nov '25", ALP: 40, Coalition: 32, Greens: 13 },
  { month: "Jan '26", ALP: 38, Coalition: 34, Greens: 12 },
  { month: "Feb '26", ALP: 39, Coalition: 33, Greens: 14 },
];

const KEY_DATES = [
  { label: "Last day to enrol", date: "2026-11-13", note: "~15 days before election" },
  { label: "Pre-poll opens",    date: "2026-11-16", note: "12 days before election" },
  { label: "Pre-poll closes",   date: "2026-11-27", note: "Friday before election" },
  { label: "ELECTION DAY",      date: "2026-11-28", note: "Polls open 8am – 6pm AEDT", highlight: true },
];

import { useCountdown, CountdownBox } from "../components/ElectionCountdown";

export default function VIC2026Page() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const countdown     = useCountdown(ELECTION_DATE);
  const enrolCountdown = useCountdown(ENROL_CLOSE);

  // Scroll to top on mount (navigation doesn't auto-scroll in React Router)
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const bg      = isDark ? "#080E1A" : "#F8FAFC";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text    = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "rgba(51,65,85,0.55)" : "rgba(226,232,240,0.85)";
  const shadowMd = isDark ? "0 4px 20px -2px rgba(0,0,0,0.35)" : "0 4px 16px -2px rgba(15,23,42,0.07)";
  const subBg   = isDark ? "#0F172A" : "#F8FAFC";

  const daysToEnrol = enrolCountdown.over ? -1 : enrolCountdown.days;
  const enrolUrgent = !enrolCountdown.over && daysToEnrol <= 14;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="vic2026-page">

      {/* Hero */}
      <div
        className="relative overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #0F172A 0%, #0a1628 40%, #0F172A 100%)",
          borderBottom: "1px solid rgba(51,65,85,0.35)",
        }}
      >
        {/* Ambient glow */}
        <div style={{
          position: "absolute", top: 0, right: "20%",
          width: 500, height: 400,
          background: "radial-gradient(ellipse at center, rgba(27,79,158,0.10) 0%, transparent 70%)",
          filter: "blur(50px)", pointerEvents: "none",
        }} />
        <div style={{ height: 3, background: "linear-gradient(90deg, #1B4F9E, #2563EB88, transparent)" }} />

        <div className="max-w-5xl mx-auto px-4 py-12 relative">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-medium mb-8 transition-all hover:opacity-70"
            style={{ color: "#FACC15", background: "none", border: "none", cursor: "pointer" }}
            data-testid="back-home-btn"
          >
            <ArrowLeft style={{ width: 15, height: 15 }} /> Home
          </button>

          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "rgba(27,79,158,0.15)", color: "#60A5FA", border: "1px solid rgba(59,130,246,0.22)" }}>
              VIC 2026
            </span>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "rgba(234,179,8,0.10)", color: "#FACC15", border: "1px solid rgba(234,179,8,0.20)" }}>
              ELECTION GUIDE
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Victoria<br />State Election 2026
          </h1>
          <p className="text-base mb-8" style={{ color: "#64748B" }}>
            Saturday, November 28, 2026 · Legislative Assembly + Legislative Council · 88 lower house seats
          </p>

          {/* Enrolment countdown pill */}
          {!enrolCountdown.over && (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{
                background: enrolUrgent ? "rgba(244,63,94,0.12)" : "rgba(234,179,8,0.10)",
                border: `1px solid ${enrolUrgent ? "rgba(244,63,94,0.25)" : "rgba(234,179,8,0.22)"}`,
              }}
              data-testid="vic-enrol-countdown"
            >
              <Bell style={{ width: 13, height: 13, color: enrolUrgent ? "#F43F5E" : "#FACC15", flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: enrolUrgent ? "#F43F5E" : "#FACC15" }}>
                Enrol by Nov 13 — <strong>{daysToEnrol} days</strong> left to enrol
              </span>
              <a
                href="https://www.vec.vic.gov.au"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 11, fontWeight: 700, color: "#CBD5E1",
                  background: "rgba(226,232,240,0.08)", border: "1px solid rgba(226,232,240,0.15)",
                  borderRadius: 99, padding: "2px 8px", textDecoration: "none",
                }}
              >
                Check enrolment →
              </a>
            </div>
          )}

          {/* Countdown */}
          {!countdown.over ? (
            <div>
              <p className="text-xs font-semibold mb-4 tracking-widest uppercase" style={{ color: "#60A5FA" }}>
                Time until polls open
              </p>
              <div className="flex items-end gap-2 sm:gap-4 flex-wrap">
                <CountdownBox value={countdown.days}    label="Days" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.hours}   label="Hours" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.minutes} label="Mins" />
                <span className="text-2xl sm:text-3xl font-black text-white mb-7 opacity-30">:</span>
                <CountdownBox value={countdown.seconds} label="Secs" />
              </div>
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{ background: "rgba(27,79,158,0.15)", border: "1px solid rgba(59,130,246,0.22)" }}
            >
              <Zap style={{ width: 15, height: 15, color: "#60A5FA" }} />
              <span className="font-semibold text-blue-300">Election Day has arrived!</span>
            </div>
          )}

          {/* Key dates */}
          <div className="flex flex-wrap gap-2.5 mt-10">
            {KEY_DATES.map((d) => (
              <div
                key={d.label}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
                style={{
                  background: d.highlight ? "rgba(27,79,158,0.15)" : "rgba(255,255,255,0.05)",
                  border: `1px solid ${d.highlight ? "rgba(59,130,246,0.25)" : "rgba(51,65,85,0.4)"}`,
                  color: d.highlight ? "#60A5FA" : "#64748B",
                }}
              >
                <Calendar style={{ width: 12, height: 12, flexShrink: 0 }} />
                <span className="font-semibold">{d.label}:</span>
                <span>{d.date}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12 space-y-16">

        {/* Head to head leaders */}
        <section data-testid="leaders-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <Users style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              Head-to-Head: Who's Running for Premier?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {VIC_LEADERS.map((leader) => (
              <div
                key={leader.id}
                className="overflow-hidden transition-all duration-200 hover:-translate-y-1"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 18, boxShadow: shadowMd }}
                data-testid={`leader-card-${leader.id}`}
              >
                <div style={{ height: 3, background: `linear-gradient(90deg, ${leader.color}, ${leader.color}66)` }} />
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-bold mb-1.5 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{leader.name}</h3>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ background: leader.color }}>
                      {leader.party}
                    </span>
                    <p className="text-xs mt-2" style={{ color: textSec }}>{leader.role}</p>
                  </div>
                  <p className="text-sm font-semibold mb-1 italic" style={{ color: leader.color }}>"{leader.slogan}"</p>
                  <p className="text-xs mb-4" style={{ color: textSec }}>
                    Electorate: {leader.electorate} · In parliament since {leader.inParlSince}
                  </p>
                  <div className="mb-3">
                    <p className="text-xs font-semibold mb-2" style={{ color: "#10B981" }}>Key achievements / promises:</p>
                    {leader.highlights.map((h) => (
                      <div key={h} className="flex items-start gap-1.5 mb-1">
                        <CheckCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2, color: "#10B981" }} />
                        <span className="text-xs" style={{ color: text }}>{h}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-semibold mb-2" style={{ color: "#F43F5E" }}>Criticisms:</p>
                    {leader.criticisms.map((c) => (
                      <div key={c} className="flex items-start gap-1.5 mb-1">
                        <XCircle style={{ width: 13, height: 13, flexShrink: 0, marginTop: 2, color: "#F43F5E" }} />
                        <span className="text-xs" style={{ color: text }}>{c}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate(`/politicians/${leader.id}`)}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all hover:-translate-y-0.5 active:scale-[0.98]"
                    style={{ background: leader.color, color: "#fff", boxShadow: `0 4px 14px ${leader.color}33`, border: "none", cursor: "pointer" }}
                    data-testid={`view-profile-${leader.id}`}
                  >
                    View Full Profile + Promise Tracker <ChevronRight style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Key Issues */}
        <section data-testid="issues-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <AlertTriangle style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              5 Key Issues Deciding the VIC Election
            </h2>
          </div>
          <div className="space-y-4">
            {VIC_ISSUES.map((issue) => (
              <div
                key={issue.id}
                className="overflow-hidden"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}
                data-testid={`issue-${issue.id}`}
              >
                <div style={{ height: 2.5, background: `linear-gradient(90deg, ${issue.color}, ${issue.color}55)` }} />
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <span className="text-2xl">{issue.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-base font-bold mb-1 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>{issue.title}</h3>
                      <p className="text-sm" style={{ color: textSec }}>{issue.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <div className="pl-3 py-2 rounded-r-xl" style={{ borderLeft: `3px solid #CC2529`, background: isDark ? "rgba(204,37,41,0.04)" : "rgba(204,37,41,0.02)" }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: "#CC2529" }}>Labor — {issue.labor.position}</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.labor.detail}</p>
                    </div>
                    <div className="pl-3 py-2 rounded-r-xl" style={{ borderLeft: `3px solid #1B4F9E`, background: isDark ? "rgba(27,79,158,0.04)" : "rgba(27,79,158,0.02)" }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: "#1B4F9E" }}>Liberal — {issue.liberal.position}</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.liberal.detail}</p>
                    </div>
                    <div className="pl-3 py-2 rounded-r-xl" style={{ borderLeft: `3px solid #10B981`, background: isDark ? "rgba(16,185,129,0.04)" : "rgba(16,185,129,0.02)" }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: "#10B981" }}>Greens — {issue.greens.position}</p>
                      <p className="text-xs" style={{ color: textSec }}>{issue.greens.detail}</p>
                    </div>
                  </div>
                  <div
                    className="flex items-start gap-2 mt-3 p-3 rounded-xl"
                    style={{ background: isDark ? "rgba(234,179,8,0.06)" : "rgba(234,179,8,0.05)", border: "1px solid rgba(234,179,8,0.14)" }}
                  >
                    <Zap style={{ width: 13, height: 13, flexShrink: 0, marginTop: 1, color: "#EAB308" }} />
                    <p className="text-xs font-medium" style={{ color: textSec }}>{issue.verdict}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Am I Ready Checklist ── */}
        <VICChecklistWidget />

        {/* Parties */}
        <section data-testid="parties-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <Building2 style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              Parties Running in VIC 2026
            </h2>
          </div>
          <div className="overflow-hidden" style={{ border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}>
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table className="w-full text-sm" style={{ minWidth: 500 }}>
                <thead>
                  <tr style={{ background: isDark ? "#0F172A" : "#F8FAFC", borderBottom: `1px solid ${borderC}` }}>
                    {["Party", "Full Name", "Leader", "2022 Seats", "2026 Outlook"].map((h) => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider" style={{ color: textSec, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {VIC_PARTIES.map((p, i) => (
                    <tr
                      key={p.name}
                      style={{ background: i % 2 === 0 ? surface : (isDark ? "rgba(15,23,42,0.4)" : "#F8FAFC") }}
                      data-testid={`party-row-${p.name.toLowerCase().replace(/\//g, "-").replace(/ /g, "-")}`}
                    >
                      <td className="py-3 px-4 font-bold border-b text-sm" style={{ color: p.color, borderColor: borderC, whiteSpace: "nowrap" }}>{p.name}</td>
                      <td className="py-3 px-4 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.full}</td>
                      <td className="py-3 px-4 border-b text-xs" style={{ color: text, borderColor: borderC, whiteSpace: "nowrap" }}>{p.leader}</td>
                      <td className="py-3 px-4 border-b text-center font-bold" style={{ color: text, borderColor: borderC }}>{p.seats2022}</td>
                      <td className="py-3 px-4 border-b" style={{ borderColor: borderC }}>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${p.color}12`, color: p.color, border: `1px solid ${p.color}22`, whiteSpace: "nowrap" }}>
                          {p.likelihood}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: textSec }}>* 2022 results: Labor 56, Liberal 26, Nationals 5, Greens 4, Ind/Other 3. 88 total lower house seats.</p>
        </section>

        {/* VIC Polls */}
        <section data-testid="vic-polls-section">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.20)" }}>
              <BarChart2 style={{ width: 18, height: 18, color: "#EAB308" }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>VIC 2026 Opinion Polls</h2>
              <p className="text-sm" style={{ color: textSec }}>Primary vote estimates — Labor leads 56–44 two-party preferred</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            {[
              { party: "ALP — Jacinta Allan", color: "#CC2529", tpp: 56 },
              { party: "Liberal — John Pesutto", color: "#1B4F9E", tpp: 44 },
            ].map((p) => (
              <div
                key={p.party}
                className="overflow-hidden"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "16px 18px" }}
                data-testid={`tpp-${p.color === "#CC2529" ? "alp" : "lib"}`}
              >
                <div style={{ height: 2, background: `linear-gradient(90deg, ${p.color}, ${p.color}44)`, marginBottom: 12 }} />
                <p className="text-xs font-semibold mb-1" style={{ color: p.color }}>{p.party}</p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-black" style={{ color: p.color, fontFamily: "Outfit, sans-serif" }}>{p.tpp}%</span>
                  <span className="text-xs" style={{ color: textSec }}>two-party-preferred (est.)</span>
                </div>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: isDark ? "rgba(51,65,85,0.4)" : "#E2E8F0" }}>
                  <div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${p.color}, ${p.color}cc)`, width: `${p.tpp}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd, padding: "20px" }} data-testid="vic-poll-trend-chart">
            <p className="text-xs font-semibold mb-4 uppercase tracking-wider" style={{ color: textSec }}>Primary Vote Trend — Oct 2025 to Feb 2026</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={VIC_TREND} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(51,65,85,0.3)" : "#E2E8F0"} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: textSec }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 55]} tick={{ fontSize: 11, fill: textSec }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => `${v}%`}
                  contentStyle={{ background: isDark ? "rgba(30,41,59,0.95)" : "#fff", border: `1px solid ${borderC}`, borderRadius: 12, color: text }}
                />
                <Bar dataKey="ALP"       fill="#CC2529" radius={[4, 4, 0, 0]} name="Labor" />
                <Bar dataKey="Coalition" fill="#1B4F9E" radius={[4, 4, 0, 0]} name="Liberal/Nat" />
                <Bar dataKey="Greens"    fill="#10B981" radius={[4, 4, 0, 0]} name="Greens" />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-5 mt-3">
              {[{ color: "#CC2529", label: "Labor" }, { color: "#1B4F9E", label: "Liberal/Nat" }, { color: "#10B981", label: "Greens" }].map((l) => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm" style={{ background: l.color }} />
                  <span className="text-xs font-medium" style={{ color: textSec }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden mt-5" style={{ border: `1px solid ${borderC}`, borderRadius: 16 }} data-testid="vic-polls-table">
            <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table className="w-full text-sm" style={{ minWidth: 600 }}>
                <thead>
                  <tr style={{ background: isDark ? "#0F172A" : "#F8FAFC", borderBottom: `1px solid ${borderC}` }}>
                    {["Pollster", "Date", "ALP", "Lib/Nat", "Greens", "Others", "ALP 2PP", "Lib 2PP"].map((h) => (
                      <th key={h} className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider" style={{ color: textSec, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {VIC_POLLS.map((p, i) => (
                    <tr key={p.pollster} style={{ background: i % 2 === 0 ? surface : (isDark ? "rgba(15,23,42,0.4)" : "#F8FAFC") }}>
                      <td className="py-2.5 px-3 font-semibold border-b text-xs" style={{ color: text, borderColor: borderC, whiteSpace: "nowrap" }}>{p.pollster}</td>
                      <td className="py-2.5 px-3 border-b text-xs" style={{ color: textSec, borderColor: borderC, whiteSpace: "nowrap" }}>{p.date}</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#CC2529", borderColor: borderC }}>{p.alp}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#1B4F9E", borderColor: borderC }}>{p.lib}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#10B981", borderColor: borderC }}>{p.grn}%</td>
                      <td className="py-2.5 px-3 border-b text-xs" style={{ color: textSec, borderColor: borderC }}>{p.oth}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#CC2529", borderColor: borderC }}>{p.two_pp_alp}%</td>
                      <td className="py-2.5 px-3 border-b font-bold text-xs" style={{ color: "#1B4F9E", borderColor: borderC }}>{p.two_pp_lib}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: textSec }}>* State polling estimates. Source: Redbridge, Essential, Newspoll VIC, Roy Morgan.</p>
        </section>

        {/* ── Marginal Seats ── */}
        <VICMarginalSeatsWidget />

        {/* How to vote */}
        <section data-testid="how-to-vote-section">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(13,92,58,0.10)", border: "1px solid rgba(13,92,58,0.18)" }}>
              <MapPin style={{ width: 18, height: 18, color: "#10B981" }} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              How to Vote in VIC 2026
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: "Check Your Enrolment", icon: CheckCircle, color: "#10B981", body: "VIC rolls close November 13, 2026 — 15 days before election. Check at vec.vic.gov.au", link: "https://www.vec.vic.gov.au", linkText: "Check enrolment →" },
              { title: "Find Your Polling Place", icon: MapPin, color: "#0D5C3A", body: "VIC uses preferential voting for the lower house. Upper house: vote for at least 5 groups above the line OR 12 candidates below.", link: "https://www.vec.vic.gov.au", linkText: "Find a polling place →" },
              { title: "Voting System", icon: Clock, color: "#1B4F9E", body: "Lower house: number ALL boxes 1, 2, 3…\nUpper house (8 regions): vote 5+ above OR 12+ below the line. Both compulsory.", link: "https://www.vec.vic.gov.au/voting/how-to-vote", linkText: "Learn how to vote →" },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="overflow-hidden transition-all duration-200 hover:-translate-y-1"
                  style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 16, boxShadow: shadowMd }}
                >
                  <div style={{ height: 2.5, background: `linear-gradient(90deg, ${card.color}, ${card.color}44)` }} />
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${card.color}10`, border: `1px solid ${card.color}20` }}>
                        <Icon style={{ width: 16, height: 16, color: card.color }} />
                      </div>
                      <h3 className="font-semibold text-sm tracking-tight" style={{ color: text }}>{card.title}</h3>
                    </div>
                    <p className="text-xs mb-3 leading-relaxed whitespace-pre-line" style={{ color: textSec }}>{card.body}</p>
                    <a
                      href={card.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold flex items-center gap-1 transition-all"
                      style={{ color: card.color }}
                    >
                      {card.linkText} <ExternalLink style={{ width: 11, height: 11 }} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Quick Tools */}
        <section data-testid="quick-tools-section">
          <h2 className="text-2xl font-bold mb-5 tracking-tight" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
            Useful Tools for VIC Voters
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "VIC Politicians", desc: "Allan, Pesutto + full team", path: "/politicians?level=state", color: "#CC2529" },
              { label: "Promise Tracker", desc: "Track what they promised VIC", path: "/promises", color: "#0D5C3A" },
              { label: "Promise Leaderboard", desc: "Who delivers on promises?", path: "/leaderboard", color: "#D97706" },
              { label: "Party Match Quiz", desc: "Which VIC party matches you?", path: "/quiz", color: "#1B4F9E" },
            ].map((t) => (
              <button
                key={t.label}
                onClick={() => navigate(t.path)}
                className="text-left transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                style={{ background: surface, border: `1px solid ${borderC}`, borderRadius: 14, boxShadow: shadowMd, padding: "16px", cursor: "pointer" }}
                data-testid={`tool-${t.label.toLowerCase().replace(/ /g, "-")}`}
              >
                <div className="w-1 h-8 rounded-full mb-3" style={{ background: `linear-gradient(180deg, ${t.color}, ${t.color}55)` }} />
                <p className="font-semibold text-sm mb-1 tracking-tight" style={{ color: t.color, fontFamily: "Outfit, sans-serif" }}>{t.label}</p>
                <p className="text-xs" style={{ color: textSec }}>{t.desc}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Official info */}
        <div
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{ background: isDark ? "rgba(30,41,59,0.6)" : "rgba(234,179,8,0.06)", border: "1px solid rgba(234,179,8,0.18)", borderRadius: 16, padding: "20px 24px" }}
          data-testid="official-vec-section"
        >
          <div>
            <p className="font-semibold text-sm mb-1" style={{ color: text }}>Official Victorian Election Authority</p>
            <p className="text-xs" style={{ color: textSec }}>All electoral information, candidate lists, and polling place finder at the Victorian Electoral Commission.</p>
          </div>
          <a
            href="https://www.vec.vic.gov.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-xl transition-all hover:-translate-y-0.5"
            style={{ background: "linear-gradient(135deg, #1B4F9E, #2563EB)", color: "#fff", boxShadow: "0 4px 14px rgba(27,79,158,0.26)", textDecoration: "none" }}
            data-testid="official-vec-link"
          >
            vec.vic.gov.au <ExternalLink style={{ width: 12, height: 12 }} />
          </a>
        </div>
      </div>
    </div>
  );
}
